import { useEffect, useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Loader2, Radio, X } from 'lucide-react'
import { assignSmartKraalEid } from '../services/api'
import { eidForm, EID_FORM_LABEL } from './SmartKraalBindingChip'

/**
 * Replace a crush-created animal's placeholder electronic id (SK-<farm>-<tag>, or the
 * tag's UHF EPC) with the LF EID the stick now reads (plan smartkraal-crush-registration
 * 4b-2). Explicit and once: every stick-read path keys on the EID, so nothing nightly
 * touches it. Used by the Profiles tab and the animal page.
 */
export default function AssignEidModal({ ranchId, animal, onClose, onDone }) {
  const [eid, setEid] = useState('')
  const [note, setNote] = useState('')
  const [error, setError] = useState('')
  const ref = useRef(null)
  useEffect(() => { setTimeout(() => ref.current?.focus(), 50) }, [])
  const form = animal?.rfid_form || eidForm(animal?.rfid_tag)
  const mut = useMutation({
    mutationFn: (body) => assignSmartKraalEid(ranchId, animal.id, body),
    onSuccess: (out) => onDone(out),
    onError: (err) => {
      const d = err?.response?.data?.detail
      setError((typeof d === 'string' ? d : (err?.message || 'Something went wrong')).replace(/^[a-z_]+:\s*/, ''))
    },
  })
  const clean = eid.replace(/\s+/g, '')
  const valid = /^\d{8,}$/.test(clean) && !/^SK-/i.test(clean)
  const name = animal?.local_name || animal?.tag_id || 'this animal'
  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg my-4">
        <div className="flex items-start justify-between p-4 border-b border-earth-200">
          <div>
            <h2 className="font-semibold text-gray-900">Assign the EID of {name}</h2>
            <p className="text-sm text-earth-500">
              Its record carries a {EID_FORM_LABEL[form] || 'placeholder'} (<span className="font-mono">{animal?.rfid_tag}</span>).
              Scan or type the LF EID the stick reads on it.
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-2 hover:bg-earth-100 rounded-lg" aria-label="Close">
            <X className="h-5 w-5 text-earth-500" />
          </button>
        </div>
        <form className="p-4 space-y-3 text-sm" onSubmit={(e) => { e.preventDefault(); if (valid) mut.mutate({ rfid_tag: clean, note: note.trim() || null }) }}>
          <div>
            <label className="label" htmlFor="assign-eid-input">LF EID *</label>
            <div className="relative">
              <Radio className="h-4 w-4 absolute left-3 top-3 text-earth-400" />
              <input id="assign-eid-input" ref={ref} value={eid} onChange={e => setEid(e.target.value)} placeholder="982…"
                     className="input pl-9 font-mono tracking-wider" inputMode="numeric" autoComplete="off" data-testid="assign-eid-input" />
            </div>
            <p className="text-xs text-earth-500 mt-1">15 digits for an ISO tag. The swap is recorded on the binding and in the animal's notes; it cannot be undone from here.</p>
          </div>
          <div>
            <label className="label" htmlFor="assign-eid-note">Note (optional)</label>
            <input id="assign-eid-note" value={note} onChange={e => setNote(e.target.value)} className="input" placeholder="e.g. read at the dip, 3 Nov" />
          </div>
          {error && <p className="text-red-700">{error}</p>}
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={!valid || mut.isPending} data-testid="assign-eid-submit">
              {mut.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Radio className="h-4 w-4 mr-2" />} Assign EID
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
